<template>
    <div v-if="canEdit" class="input-group mb-3">
        <input type="text" class="form-control" placeholder="OR NO." v-model="orNo">
        <input placeholder="Date" class="form-control" type="text" v-model="dateInput" @focus="setDateType"
            @blur="setTextType">
        <input type="text" class="form-control" placeholder="AMOUNT" v-model="amount">
    </div>

    <div ref="certificateContainer" class="certificate-container">
        <!-- Decorative border -->
        <div class="certificate-border"></div>

        <!-- Optional watermark -->
        <div class="certificate-watermark">OFFICIAL DOCUMENT</div>

        <!-- Optional seal -->
        <div class="certificate-seal"></div>

        <div class="certificate-content">
            <!-- Header -->
            <div class="certificate-header">
                <img :src="oboImage" alt="OBO Logo" class="header-logo">
                <br>
                <img v-if="qrCodeDataUrl" :src="qrCodeDataUrl" alt="QR Code" class="qr-code-image" width="120">

                <!-- Fallback to SOA image if no QR code -->
                <img v-else-if="currentSoaImage" :src="currentSoaImage" alt="SOA Image" class="sub-logo" width="120">
                <h5 class="document-title">OBO CLEARANCE</h5>
            </div>

            <!-- Certificate number -->
            <div class="certificate-number">
                <b>BIN: {{ currentApplication?.application?.bin }}</b>
            </div>
            <!-- Body -->
            <div class="certificate-body">
                <TheSignature font-size="15px" max-img-height="100px" image-offset-y="-70"
                    :position="JSON.parse(sig.signature_position_json)" :sig="sig" v-for="sig in signatories"
                    :key="`sig${sig.official_id}`" />
                <div class="document-content">
                    <p class="document-text">
                        <b>TO: MICHAEL S. PASANA</b> <br>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;BPLS In-Charge
                        <br>
                        <br>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Clearance is
                        hereby granted to <u><b>{{ ownerFullName }}</b></u> to
                        proceed his/her
                        <u><b>{{ currentApplication?.application?.type }}</b></u> permit
                        application for <u><b>{{ currentApplication?.permitInfo?.business_name }}</b></u>
                        located at <u><b>{{ businessAddress }}</b></u>
                        <br>
                        <br>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;This
                        clearance is issued and revocable subject for the compliance of the National
                        Building Code of the Philippines and any other
                        applicable laws and ordinances of the National Government Municipality and Barangay
                        where the subject business is located.
                        <br>
                        <br>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Issued this
                        <u><b>{{ formattedDate }}</b></u> at the Office of the Building Official
                        Palompon, Leyte.
                        <br>
                        <br>
                        <br>
                    </p>
                </div>
            </div>

            <!-- Footer -->
            <div class="document-footer">
                <div class="row">
                    <div class="col-md-8 payment-details">
                        <b>OR NO:</b> {{ orNo || 'N/A' }}<br>
                        <b>DATE:</b> {{ formattedDate }}<br>
                        <b>AMOUNT:</b> {{ formattedAmount }}<br>
                        <b>PLACE:</b> Palompon, Leyte<br>
                    </div>
                    <div class="col-md-4 signature-section">
                        <div class="signature-container">
                            <!-- <img v-if="signaturePath" :src="signaturePath" alt="Signature" class="signature-image">
                            <div class="signature-line"></div>
                            <div class="signatory-name">{{ userSignatures?.OBO?.signatory_name }}</div>
                            <div class="signatory-title">Municipal Building Official</div> -->
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="action-buttons">
        <button v-if="canPrint" @click="printAsImage" class="btn btn-primary me-2">
            Print Certificate
        </button>
        <button v-if="canPrint" @click="downloadAsImage" class="btn btn-success">
            Download Certificate
        </button>
        <button v-if="orNo && dateInput && amount && canSaveToIssue" class="btn btn-warning" @click="saveToIssue">
            SAVE TO ISSUE
        </button>
    </div>
</template>

<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import oboImage from '@/assets/img-headers/obo.png';
import html2canvas from 'html2canvas'; // Install: npm install html2canvas
import QRCode from 'qrcode';
import TheSignature from '../docs/TheSignature.vue';
import signatureService from '@/services/signatureService';
import certificateService from '@/services/certificateService';
import confirmationService from '@/services/confirmationService';
import toastService from '@/services/toastService';
import spinnerService from '@/services/spinnerService';
import Formatters from '@/utils/formatters';
// Props
//eslint-disable-next-line
const props = defineProps({
    currentApplication: {
        type: Object,
        default: () => ({})
    },
    currentSoa: {
        type: Object,
        default: () => ({})
    },
    userSignatures: {
        type: Object,
        default: () => ({})
    }
});
const signatories = ref([]);
// Emits
//eslint-disable-next-line
const emit = defineEmits(['print', 'save']);

// Reactive data
const orNo = ref('');
const dateInput = ref('');
const amount = ref('');

const ownerFullName = computed(() => {
    const app = props.currentApplication;
    const firstName = app.permitInfo?.owner_firstname || '';
    const middleName = app.permitInfo?.owner_middlename || '';
    const lastName = app.permitInfo?.owner_lastname || '';

    // Format: FirstName [MiddleInitial.] LastName
    let fullName = firstName.trim();

    // Add middle initial if middle name exists and is not empty/null/NA
    if (middleName &&
        middleName.trim() !== '' &&
        middleName.trim().toLowerCase() !== 'n/a' &&
        middleName.trim().toLowerCase() !== 'null' &&
        middleName.trim().toLowerCase() !== 'none') {

        // Take first character of middle name and add period
        const middleInitial = middleName.trim().charAt(0).toUpperCase() + '.';
        fullName += ' ' + middleInitial;
    }

    // Add last name
    fullName += ' ' + lastName.trim();

    return fullName.trim();
});

const businessAddress = computed(() => {
    const app = props.currentApplication;
    const parts = [
        app.businessInfo?.building_name,
        app.businessInfo?.operation_block_no_lot_no,
        app.businessInfo?.operation_street,
        app.businessInfo?.operation_subdivision,
        app.businessInfo?.operation_barangay,
        app.businessInfo?.operation_city,
        app.businessInfo?.operation_province
    ].filter(part => part && part !== 'None' && part !== 'N/A' && part !== 'null');
    return parts.join(' ');
});


const currentCertCode = ref("");
const canSaveToIssue = computed(() => {
    if (!props.currentApplication.application.application_id) return false;

    if (props.currentApplication.issuedCerts.findIndex(x => x.type === currentCertCode.value.toUpperCase()) === -1)
        return true;

    return false;
});

const canPrint = computed(() => {
    if (props.currentApplication.issuedCerts.findIndex(x => x.type === currentCertCode.value.toUpperCase()) > -1)
        return true;

    return false;
});


const formattedDate = computed(() => {
    if (!dateInput.value) return 'N/A';

    try {
        const date = new Date(dateInput.value);
        if (isNaN(date.getTime())) {
            // Try to parse as string
            const parts = dateInput.value.split('/');
            if (parts.length === 3) {
                const [month, day, year] = parts;
                return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
            }
            return dateInput.value;
        }

        // Format as mm/dd/yyyy
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();

        return `${month}/${day}/${year}`;
    } catch (error) {
        return dateInput.value;
    }
});

const formattedAmount = computed(() => {
    if (!amount.value) return '₱0.00';

    const num = parseFloat(amount.value);
    if (isNaN(num)) return amount.value;

    return new Intl.NumberFormat('en-PH', {
        style: 'currency',
        currency: 'PHP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(num);
});

// Methods
const setDateType = (event) => {
    event.target.type = 'date';
};

const setTextType = (event) => {
    if (!event.target.value) {
        event.target.type = 'text';
    }
};
//eslint-disable-next-line
const printObo = () => {
    emit('print', 'OBO');
};
//eslint-disable-next-line
const saveToIssue = async () => {
    const confirmed = await confirmationService.show("Are you sure you want to save and issue?");
    if (!confirmed) return;
    const payload = {
        ref_date: dateInput.value,
        ref_no: orNo.value,
        type: 'OBO',
        amount: parseFloat(amount.value) || 0,
        period: props.currentApplication?.application?.year,
        application_id: props.currentApplication?.application?.application_id
    };
    spinnerService.show();
    const resp = await certificateService.saveToIssue(payload);
    if (resp.success) {
        toastService.success("Certificate issued successfully!");
        emit("save-success");
    }
    else {
        toastService.error(resp.message);
    }
    spinnerService.hide();

};

/* eslint-disable */

const certificateContainer = ref(null);

const hiddenCanvas = ref(null);
const printImage = ref(null);

const printAsImage = async () => {
    if (!certificateContainer.value) return;

    try {
        // Show loading
        const loading = showLoading('Generating certificate image...');

        // Convert certificate to canvas
        const canvas = await html2canvas(certificateContainer.value, {
            scale: 2, // Higher quality
            backgroundColor: '#ffffff',
            useCORS: true, // For external images
            allowTaint: true,
            logging: false,
            onclone: (clonedDoc) => {
                // Ensure all images are loaded in cloned document
                const images = clonedDoc.querySelectorAll('img');
                return Promise.all(
                    Array.from(images).map(img => {
                        if (img.complete) return Promise.resolve();
                        return new Promise((resolve, reject) => {
                            img.onload = resolve;
                            img.onerror = reject;
                        });
                    })
                );
            }
        });

        // Convert canvas to image
        const imageData = canvas.toDataURL('image/png', 1.0);

        // Print the image
        await printImageData(imageData);

        loading.hide();
    } catch (error) {
        console.error('Error generating certificate image:', error);
        alert('Failed to generate certificate. Please try again.');
    }
};
const qrCodeDataUrl = ref('');
const generateQRCodeData = () => {
    const app = props.currentApplication;
    if (!app || !app.application) return null;

    // Create a structured data object for the QR code
    const qrData = {
        type: 'OBO_CLEARANCE',
        bin: app.application.bin || '',
        businessName: app.bInfoPermit?.business_name || '',
        ownerName: `${app.bInfoPermit?.owner_firstname || ''} ${app.bInfoPermit?.owner_lastname || ''}`.trim(),
        date: new Date().toISOString().split('T')[0],
        location: `${app.bInfo?.operation_barangay || ''}, ${app.bInfo?.operation_city || ''}`,
        status: 'APPROVED'
    };

    return JSON.stringify(qrData);
};

const generateQRCode = async () => {
    try {
        const qrData = generateQRCodeData();

        if (!qrData) {
            qrCodeDataUrl.value = '';
            return;
        }

        // Generate QR code as data URL
        const url = await QRCode.toDataURL(qrData, {
            width: 120,
            margin: 2,
            color: {
                dark: '#000000',  // Black dots
                light: '#FFFFFF'  // White background
            },
            errorCorrectionLevel: 'H'  // High error correction
        });

        qrCodeDataUrl.value = url;

    } catch (error) {
        console.error('Error generating QR code:', error);
        qrCodeDataUrl.value = '';
    }
};

const downloadAsImage = async () => {
    if (!certificateContainer.value) return;

    try {
        const canvas = await html2canvas(certificateContainer.value, {
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true
        });

        // Create download link
        const link = document.createElement('a');
        link.download = `certificate-${new Date().getTime()}.png`;
        link.href = canvas.toDataURL('image/png', 1.0);
        link.click();
    } catch (error) {
        console.error('Error downloading certificate:', error);
    }
};

const printImageData = (imageData) => {
    return new Promise((resolve) => {
        // Create HTML content as data URL
        const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Print Certificate</title>
        <style>
          @page { margin: 0; }
          body { margin: 0; }
          img { width: 100%; height: auto !important; }
        </style>
      </head>
      <body>
        <img src="${imageData}" 
             onload="setTimeout(() => { window.print(); setTimeout(() => window.close(), 1000); }, 500);"
             onerror="window.close()">
        <script>
          window.onafterprint = () => setTimeout(() => window.close(), 500);
          setTimeout(() => { if(!window.closed) window.close(); }, 10000);
        <\/script>
      </body>
      </html>
    `;

        // Encode HTML as data URL
        const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
        const htmlUrl = URL.createObjectURL(htmlBlob);

        // Open as new tab
        const newTab = window.open(htmlUrl, '_blank');

        if (!newTab) {
            // Fallback: Show in current window
            const printIframe = document.createElement('iframe');
            printIframe.style.cssText = 'position:fixed;width:0;height:0;border:none;';
            printIframe.srcdoc = htmlContent;
            document.body.appendChild(printIframe);

            printIframe.onload = () => {
                setTimeout(() => {
                    printIframe.contentWindow.print();
                    setTimeout(() => {
                        document.body.removeChild(printIframe);
                        URL.revokeObjectURL(htmlUrl);
                    }, 1000);
                }, 500);
            };
        } else {
            // Clean up URL after tab opens
            setTimeout(() => URL.revokeObjectURL(htmlUrl), 1000);
        }

        resolve();
    });
};
const showLoading = (message) => {
    const loading = document.createElement('div');
    loading.innerHTML = `
    <div style="
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0,0,0,0.7);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 9999;
      color: white;
      font-size: 18px;
      flex-direction: column;
    ">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <div style="margin-top: 20px">${message}</div>
    </div>
  `;
    document.body.appendChild(loading);

    return {
        hide: () => {
            if (loading.parentNode) {
                loading.parentNode.removeChild(loading);
            }
        }
    };
};
const preProcessCertInfo = (certInfo) => {
    if (!certInfo) return;
    orNo.value = certInfo.ref_no;
    dateInput.value = Formatters.formatDateForInput(certInfo.ref_date);
    amount.value = certInfo.amount;
}

const getSignatories = async (code) => {
    currentCertCode.value = code;
    const certInfo = props.currentApplication.issuedCerts.find(x => x.type === code.toUpperCase());
    preProcessCertInfo(certInfo);
    signatories.value = await signatureService.getSignatoriesByCode(code);
}
defineExpose({ getSignatories });

// Initialize QR code when component mounts
onMounted(() => {
    // Initialize QR code canvas if needed
    const canvas = document.getElementById('qr-code-obo');
    if (canvas) {
        // QR code generation logic here
        console.log('QR Code canvas ready');
    }
});

// Watch for changes to clear fields if needed
watch(() => props.currentApplication, (newApp) => {
    // Clear fields if application changes
    if (!newApp || !newApp.application) {
        orNo.value = '';
        dateInput.value = '';
        amount.value = '';
    }
    generateQRCode();
}, { deep: true });
</script>

<style scoped>
/* Certificate container - fixed width for all screens */
.certificate-container {
    background-color: white;
    padding: 40px;
    width: 1000px;
    min-width: 1000px;;
    /* Fixed width for certificate */
    margin: 0 auto;
    /* Center the certificate */
    min-height: 1400px;
    height: 1400px;
    /* Fixed height for A4-like layout */
    box-sizing: border-box;
    position: relative;
    page-break-inside: avoid;
    font-family: 'Times New Roman', Times, serif;
    /* Prevent page break inside certificate */
}

/* Certificate content area */
.certificate-content {
    width: 100%;
    height: 100%;
    position: relative;
}

/* Certificate border decoration */
.certificate-border {
    position: absolute;
    top: 20px;
    left: 20px;
    right: 20px;
    bottom: 20px;
    border: 3px double #b71c1c;
    /* Double red border */
    pointer-events: none;
    z-index: 1;
}

/* Header section */
.certificate-header {
    text-align: center;
    margin-bottom: 30px;
    padding-bottom: 20px;
    border-bottom: 2px solid #b71c1c;
    position: relative;
    z-index: 2;
}

.header-logo {
    width: 900px;
    max-width: 90%;
    height: auto;
    margin: 0 auto 15px;
    display: block;
}

.sub-logo {
    width: 120px;
    height: auto;
    margin: 10px auto;
    display: block;
}

.document-title {
    font-family: 'Times New Roman', Times, serif;
    color: darkblue;
    font-weight: bold;
    margin: 10px 0;
    font-size: 24px;
    letter-spacing: 2px;
    text-transform: uppercase;
}

/* Certificate number */
.certificate-number {
    text-align: right;
    margin-bottom: 20px;
    font-family: 'Times New Roman', Times, serif;
    font-size: 17px;
    font-weight: bold;
    color: #333;
    position: relative;
    z-index: 2;
}

/* Certificate body */
.certificate-body {
    padding: 0 50px;
    position: relative;
    z-index: 2;
}

.document-text {
    text-align: justify;
    text-justify: inter-word;
    color: #222;
    line-height: 1.8;
    font-size: 18px;
    font-family: 'Times New Roman', Times, serif;
    margin-bottom: 30px;
}

.document-text u {
    text-decoration: none;
    border-bottom: 1px solid #333;
    padding-bottom: 1px;
}

/* Document footer */
.document-footer {
    margin-top: 60px;
    padding: 0 50px;
    position: relative;
    z-index: 2;
}

.payment-details {
    color: #222;
    font-size: 16px;
    line-height: 2;
    font-family: 'Times New Roman', Times, serif;
    margin-bottom: 20px;
}

.signature-section {
    position: relative;
    min-height: 200px;
}

.signature-container {
    position: relative;
    text-align: center;
    padding-top: 80px;
}

.signature-line {
    width: 300px;
    height: 1px;
    background-color: #333;
    margin: 0 auto 5px;
}

.signature-image {
    position: absolute;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    width: 200px;
    height: auto;
    opacity: 0.9;
    z-index: 1;
}

.signatory-name {
    font-family: 'Times New Roman', Times, serif;
    font-size: 18px;
    font-weight: bold;
    color: #222;
    margin-top: 10px;
}

.signatory-title {
    font-family: 'Times New Roman', Times, serif;
    font-size: 16px;
    color: #555;
    margin-top: 5px;
}

/* Input controls (non-printable) */
.input-group {
    gap: 10px;
    margin-bottom: 20px;
    max-width: 1000px;
    margin-left: auto;
    margin-right: auto;
}

.input-group .form-control {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    padding: 8px 12px;
}

.action-buttons {
    text-align: center;
    margin-top: 20px;
    margin-bottom: 30px;
}

/* PRINT-ONLY STYLES */
@media print {
    @page {
        size: A4 portrait;
        margin: 0;
    }

    body,
    html {
        margin: 0;
        padding: 0;
        background: white !important;
        width: 210mm;
        /* A4 width */
        min-height: 297mm;
        /* A4 height */
    }

    .certificate-container {
        width: 100% !important;
        max-width: 100% !important;
        min-height: 297mm !important;
        /* Full A4 height */
        margin: 0 !important;
        padding: 20mm !important;
        /* Print margins */
        border: none !important;
        box-shadow: none !important;
        page-break-after: always;
        break-inside: avoid;
    }

    /* Hide all non-certificate elements */
    body * {
        visibility: hidden;
    }

    .certificate-container,
    .certificate-container * {
        visibility: visible;
    }

    .certificate-container {
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
    }

    /* Hide interactive elements */
    .input-group,
    .action-buttons,
    .btn,
    button {
        display: none !important;
    }

    /* Print-specific adjustments */
    .certificate-border {
        border: 3px double #b71c1c !important;
    }

    .header-logo {
        width: 160mm !important;
        /* Fit within A4 */
        max-width: 90% !important;
    }

    .document-text {
        font-size: 16pt !important;
        line-height: 1.8 !important;
    }

    /* Ensure text is black for printing */
    .document-title,
    .document-text,
    .payment-details,
    .signatory-name,
    .signatory-title,
    .certificate-number {
        color: #000 !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }

    /* Prevent page breaks inside important sections */
    .certificate-header,
    .certificate-body,
    .document-footer {
        page-break-inside: avoid;
        break-inside: avoid;
    }

    /* Adjust spacing for print */
    .certificate-body {
        padding: 0 30px !important;
    }

    .document-footer {
        margin-top: 40px !important;
        padding: 0 30px !important;
    }

    /* Ensure signature appears */
    .signature-image {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
    }
}

/* SCREEN-ONLY STYLES (for preview) */
@media screen {
    .certificate-container {
        max-height: 90vh;
    }

    /* Scrollbar styling for certificate preview */
    .certificate-container::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }

    .certificate-container::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
    }

    .certificate-container::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 4px;
    }

    .certificate-container::-webkit-scrollbar-thumb:hover {
        background: #555;
    }
}

/* Fix for printing background colors */
@media print and (color) {
    * {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }
}

/* Watermark style (optional) */
.certificate-watermark {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-45deg);
    font-size: 80px;
    color: rgba(183, 28, 28, 0.05);
    font-weight: bold;
    white-space: nowrap;
    z-index: 0;
    pointer-events: none;
    user-select: none;
}

/* Seal/stamp effect */
.certificate-seal {
    position: absolute;
    bottom: 100px;
    right: 100px;
    width: 150px;
    height: 150px;
    border: 3px solid #b71c1c;
    border-radius: 50%;
    opacity: 0.1;
    z-index: 0;
    pointer-events: none;
}
</style>